import { useEffect, useState } from "react";
import AuthUser from "./AuthUser";
import TaskSkeleton from "./TaskSkeleton";
import moment from "moment";
import * as XLSX from "xlsx";

// Preferred status order
const STATUS_ORDER = [
  "Pending",
  "Completed",
  "Hold-In-HQ",
  "Hold-To-HQ",
  "Hold-waiting-RM",
  "Pend-Cus-Confir",
  "Ready",
  "Ready waiting po",
];

const HIDDEN_STATUSES = new Set(["Delivered", "0", ""]);

const BRANCH_ORDER = ["K1", "K2", "OTHER JC", "AMC"];

const AMC_FIELDS = [
  { key: "total", label: "Total" },
  { key: "resolve_no", label: "Pending" },
  { key: "resolve_yes", label: "Resolve Yes" },
  { key: "contacted", label: "Contacted" },
  { key: "not_contacted", label: "Not Contacted" },
  { key: "assigned", label: "Assigned" },
  { key: "unassigned", label: "Unassigned" },
  { key: "online", label: "Online" },
  { key: "offline", label: "Offline" },
  { key: "priority_high", label: "High" },
  { key: "priority_medium", label: "Medium" },
  { key: "priority_low", label: "Low" },
  { key: "bill_value", label: "Bill Value" },
];

const EMPTY_AMC = AMC_FIELDS.reduce((acc, f) => {
  acc[f.key] = 0;
  return acc;
}, {});

export default function JcBranchReport() {
  const { http } = AuthUser();
  const [data, setData] = useState([]);
  const [amc, setAmc] = useState(EMPTY_AMC);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    http.get("/get_jc_branch_status_summary")
      .then((res) => {
        setIsLoading(false);
        setData(Array.isArray(res.data.data) ? res.data.data : []);
        setAmc(res.data.amc ? { ...EMPTY_AMC, ...res.data.amc } : EMPTY_AMC);
      })
      .catch(() => setIsLoading(false));
  }, []);

  // Build ordered unique branches and statuses from data (Delivered hidden)
  const branchesInData = [...new Set(data.map(r => r.branch_name))];
  const jcBranches = [
    ...BRANCH_ORDER.filter((b) => b !== "AMC" && branchesInData.includes(b)),
    ...branchesInData.filter((b) => !BRANCH_ORDER.includes(b) && b !== "AMC").sort(),
  ];
  const statusesInData = [...new Set(
    data
      .map(r => r.status)
      .filter(s => s != null && !HIDDEN_STATUSES.has(String(s)))
  )];
  const allStatuses = [
    ...STATUS_ORDER.filter(s => statusesInData.includes(s)),
    ...statusesInData.filter(s => !STATUS_ORDER.includes(s)).sort(),
  ];
  if (Number(amc.resolve_no || 0) > 0 && !allStatuses.includes("Pending")) {
    allStatuses.unshift("Pending");
  }
  const statuses = allStatuses.filter((s) => s !== "Not_Taken");
  const hasNotTaken = allStatuses.includes("Not_Taken");

  // Pivot: branch -> status -> count
  const pivot = {};
  jcBranches.forEach(b => { pivot[b] = {}; });
  data.forEach(r => {
    if (HIDDEN_STATUSES.has(String(r.status ?? ""))) return;
    if (!pivot[r.branch_name]) pivot[r.branch_name] = {};
    pivot[r.branch_name][r.status] = r.cnt;
  });

  // AMC row: Resolve No = Pending
  const amcPending = Number(amc.resolve_no || 0);
  pivot["AMC"] = { Pending: amcPending };

  const displayBranches = [...jcBranches, "AMC"];

  // Row totals exclude Not_Taken
  const rowTotals = {};
  displayBranches.forEach((b) => {
    rowTotals[b] = statuses.reduce((s, st) => s + Number(pivot[b]?.[st] || 0), 0);
  });

  const colTotals = {};
  statuses.forEach((st) => {
    colTotals[st] = displayBranches.reduce((s, b) => s + Number(pivot[b]?.[st] || 0), 0);
  });
  const notTakenTotals = {};
  displayBranches.forEach((b) => {
    notTakenTotals[b] = Number(pivot[b]?.Not_Taken || 0);
  });
  const notTakenGrand = displayBranches.reduce((s, b) => s + notTakenTotals[b], 0);

  const grandTotal = displayBranches.reduce((s, b) => s + rowTotals[b], 0);

  const formatVal = (key, val) => {
    if (key === "bill_value") {
      return Number(val || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    }
    const n = Number(val || 0);
    return n > 0 ? n.toLocaleString("en-IN") : "-";
  };

  const handleExcel = () => {
    const jcHeader = ["Branch", ...statuses, "Total", ...(hasNotTaken ? ["Not_Taken"] : [])];
    const jcRows = displayBranches.map((b) => ({
      Branch: b,
      ...Object.fromEntries(statuses.map((st) => [st, pivot[b]?.[st] || 0])),
      Total: rowTotals[b],
      ...(hasNotTaken ? { Not_Taken: notTakenTotals[b] } : {}),
    }));
    jcRows.push({
      Branch: "TOTAL",
      ...Object.fromEntries(statuses.map((st) => [st, colTotals[st]])),
      Total: grandTotal,
      ...(hasNotTaken ? { Not_Taken: notTakenGrand } : {}),
    });

    const amcRow = {
      Branch: "AMC",
      ...Object.fromEntries(
        AMC_FIELDS.map((f) => [f.label, amc[f.key] ?? 0])
      ),
    };

    const wb = XLSX.utils.book_new();
    const jcWs = XLSX.utils.json_to_sheet(jcRows, { header: jcHeader });
    XLSX.utils.book_append_sheet(wb, jcWs, "JC Branch");

    const amcHeader = ["Branch", ...AMC_FIELDS.map((f) => f.label)];
    const amcWs = XLSX.utils.json_to_sheet([amcRow], { header: amcHeader });
    XLSX.utils.book_append_sheet(wb, amcWs, "AMC Branch");

    XLSX.writeFile(wb, `jc_branch_report_${moment().format("YYYYMMDD_HHmm")}.xlsx`);
  };

  const statusColor = (st) => {
    if (st === "Pending") return "#fff3cd";
    if (st === "Completed") return "#d1e7dd";
    return "#f0f4ff";
  };

  const amcFieldColor = (key, val) => {
    if (!val || Number(val) === 0) return "";
    if (key === "resolve_no" || key === "unassigned" || key === "not_contacted") return "#fff3cd";
    if (key === "resolve_yes" || key === "contacted" || key === "assigned") return "#d1e7dd";
    return "#f0f4ff";
  };

  return (
    <div className="container-fluid mt-3">
      <style>{`
        .jc-branch-report-table-wrap {
          max-width: 50%;
          width: 100%;
        }
        .jc-branch-report-table-wrap table {
          width: 100%;
        }
        @media (max-width: 992px) {
          .jc-branch-report-table-wrap {
            max-width: 100%;
          }
        }
      `}</style>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-0 fw-bold">JC Branch Report</h4>
          <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
            JC count per branch grouped by status (Delivered hidden)
          </p>
        </div>
        <button
          className="btn btn-sm btn-outline-success"
          disabled={data.length === 0 && !amc.total}
          onClick={handleExcel}
        >⬇ Excel</button>
      </div>

      {isLoading ? (
        <TaskSkeleton />
      ) : (
        <>
          {data.length === 0 && !amc.total ? (
            <p className="text-center text-muted mt-4">No JC data found.</p>
          ) : (
            <div className="table-responsive mb-4 jc-branch-report-table-wrap">
              <table className="table table-bordered text-center align-middle mb-0">
                <thead>
                  <tr style={{ backgroundColor: "#0d6efd", color: "#fff" }}>
                    <th className="text-start" style={{ minWidth: "110px" }}>Branch</th>
                    {statuses.map(st => (
                      <th key={st} style={{ fontSize: "12px", minWidth: "80px" }}>{st}</th>
                    ))}
                    <th style={{ minWidth: "70px" }}>Total</th>
                    {hasNotTaken && (
                      <th style={{ fontSize: "12px", minWidth: "90px" }}>Not_Taken</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {displayBranches.map((b) => (
                    <tr key={b} style={b === "AMC" ? { backgroundColor: "#f0fdf4" } : undefined}>
                      <td className="text-start fw-bold text-uppercase">{b}</td>
                      {statuses.map((st) => {
                        const val = Number(pivot[b]?.[st] || 0);
                        return (
                          <td
                            key={st}
                            style={{
                              backgroundColor: val > 0 ? statusColor(st) : "",
                              fontWeight: val > 0 ? "600" : "normal",
                              color: val > 0 ? "#000" : "#aaa",
                            }}
                          >
                            {val > 0 ? val : "-"}
                          </td>
                        );
                      })}
                      <td className="fw-bold text-primary">{rowTotals[b].toLocaleString("en-IN")}</td>
                      {hasNotTaken && (
                        <td
                          style={{
                            backgroundColor: notTakenTotals[b] > 0 ? "#f3e8ff" : "",
                            fontWeight: notTakenTotals[b] > 0 ? "600" : "normal",
                            color: notTakenTotals[b] > 0 ? "#000" : "#aaa",
                          }}
                        >
                          {notTakenTotals[b] > 0 ? notTakenTotals[b] : "-"}
                        </td>
                      )}
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: "#dce7ff", fontWeight: "bold" }}>
                    <td className="text-start">TOTAL</td>
                    {statuses.map((st) => (
                      <td key={st}>{colTotals[st].toLocaleString("en-IN")}</td>
                    ))}
                    <td className="text-primary">{grandTotal.toLocaleString("en-IN")}</td>
                    {hasNotTaken && <td>{notTakenGrand.toLocaleString("en-IN")}</td>}
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          <div className="d-flex justify-content-between align-items-center mb-2 mt-2">
            <div>
              <h5 className="mb-0 fw-bold">AMC Branch</h5>
              <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                AMC ticket counts across all fields
              </p>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-bordered text-center align-middle">
              <thead>
                <tr style={{ backgroundColor: "#198754", color: "#fff" }}>
                  <th className="text-start" style={{ minWidth: "90px" }}>Branch</th>
                  {AMC_FIELDS.map(f => (
                    <th key={f.key} style={{ fontSize: "12px", minWidth: "78px" }}>{f.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-start fw-bold">AMC</td>
                  {AMC_FIELDS.map(f => {
                    const raw = amc[f.key] ?? 0;
                    const display = formatVal(f.key, raw);
                    const hasVal = Number(raw) > 0;
                    return (
                      <td
                        key={f.key}
                        style={{
                          backgroundColor: amcFieldColor(f.key, raw),
                          fontWeight: hasVal ? "600" : "normal",
                          color: hasVal ? "#000" : "#aaa",
                        }}
                      >
                        {display}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
